// LSP `textDocument/definition` → CodeMirror navigation adapter.
//
// Two interaction surfaces share the same action: Cmd / Ctrl-click on
// an identifier, and the F12 keybinding (the de-facto editor standard
// for goto-definition across VS Code, JetBrains, Vim LSP plugins, etc.).
// Both end up calling `goto()`, which asks the LSP, normalises the
// response to a single `Location`, and moves the cursor when the
// definition is in the document we're already editing.
//
// Cross-file definitions are deliberately ignored for m5a. The Free
// editor only mounts one synthetic document (`memory:///nowline-free.nowline`);
// `include` references resolve against a virtual filesystem that the
// editor doesn't surface as tabs yet. The right place to wire that
// surface is Pro's multi-pane shell (m6) — flagged as a TODO below.

import { EditorSelection, type Extension } from '@codemirror/state';
import { type Command, EditorView, type KeyBinding, keymap } from '@codemirror/view';
import type { Definition, Location, LocationLink } from 'vscode-languageserver-protocol';
import type { LspClient } from '../client.ts';
import { lspPositionToCmPos } from '../positions.ts';

export function lspDefinition(client: LspClient): Extension {
    const goto = async (view: EditorView, pos: number): Promise<void> => {
        const result = await client.definition(view.state, pos).catch(() => null);
        const target = pickInDocLocation(result, client.uri);
        if (!target) return;
        const cmPos = lspPositionToCmPos(view.state.doc, target.start);
        view.dispatch({
            selection: EditorSelection.cursor(cmPos),
            scrollIntoView: true,
        });
        view.focus();
    };

    const f12: Command = (view) => {
        const head = view.state.selection.main.head;
        void goto(view, head);
        return true;
    };
    const keys: KeyBinding[] = [{ key: 'F12', run: f12 }];

    const mouse = EditorView.domEventHandlers({
        mousedown(event, view) {
            // Modifier-click only — bare clicks must continue to set
            // the cursor / drag-select as usual.
            const isModClick = event.button === 0 && (event.metaKey || event.ctrlKey);
            if (!isModClick) return false;
            const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
            if (pos == null) return false;
            event.preventDefault();
            void goto(view, pos);
            return true;
        },
    });

    return [keymap.of(keys), mouse];
}

// Normalise the three possible response shapes
// (`Location | Location[] | LocationLink[] | null`) into the single
// target range we'll move the cursor to. Filters out cross-file results
// for m5a — the editor only owns one document, so a goto into a
// different URI would silently misalign the selection.
function pickInDocLocation(
    result: Definition | LocationLink[] | null,
    docUri: string,
): { start: { line: number; character: number }; end: { line: number; character: number } } | null {
    if (!result) return null;
    const candidates: Array<Location | LocationLink> = Array.isArray(result) ? result : [result];
    for (const c of candidates) {
        if (isLocation(c)) {
            if (c.uri === docUri) return c.range;
        } else if (c.targetUri === docUri) {
            return c.targetSelectionRange ?? c.targetRange;
        }
    }
    return null;
}

function isLocation(loc: Location | LocationLink): loc is Location {
    return (loc as Location).uri !== undefined && (loc as Location).range !== undefined;
}
